import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { FileDown, RotateCcw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { FeeReport } from "@/components/FeeReport";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, downloadReceipt, errMsg, fmtBtc, fmtUsd } from "@/lib/api";

const FILTERS = ["all", "buy", "sell", "withdraw"];
const TYPE_CLS = { buy: "text-emerald-400", sell: "text-red-400", withdraw: "text-[#F7931A]" };
const utc = (iso) => new Date(iso).toISOString().replace("T", " ").slice(0, 19) + " UTC";

const StatCard = ({ label, value, testid }) => (
  <div className="card p-5">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">{label}</p>
    <p className="mt-2 font-mono text-xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

const RecordRow = ({ t, onDelete }) => (
  <tr className="border-t border-[#212638] align-top transition-colors hover:bg-[#1C2030]" data-testid={`admin-record-row-${t.tx_id}`}>
    <td className="py-3"><p className="font-semibold">{t.user_name || "—"}</p><p className="text-xs text-[#94A3B8]">{t.user_email}</p></td>
    <td className={`py-3 font-semibold capitalize ${TYPE_CLS[t.type]}`}>{t.type}</td>
    <td className="py-3 font-mono">{fmtUsd(t.usd_amount)}<p className="text-xs text-[#64748B]">fee {fmtUsd(t.fee_usd || 0)}</p></td>
    <td className="hidden py-3 font-mono text-xs lg:table-cell">{t.type === "withdraw" ? "—" : fmtBtc(t.btc_amount)}</td>
    <td className="hidden py-3 text-xs text-[#94A3B8] md:table-cell">{t.method}<p className="capitalize text-[#64748B]">{t.status}</p></td>
    <td className="py-3 font-mono text-xs" data-testid={`admin-record-time-${t.tx_id}`}>{utc(t.created_at)}</td>
    <td className="hidden py-3 font-mono text-[11px] text-[#94A3B8] xl:table-cell" data-testid={`admin-record-signature-${t.tx_id}`}>{t.signature}</td>
    <td className="py-3 text-right">
      <button onClick={() => downloadReceipt(t.tx_id, true).catch((e) => toast.error(errMsg(e)))} data-testid={`admin-receipt-download-${t.tx_id}`}
        className="inline-flex items-center gap-1 rounded-lg border border-[#212638] px-2.5 py-1.5 text-xs text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-[#F7931A]">
        <FileDown size={13} /> PDF
      </button>
      <button onClick={() => onDelete(t)} data-testid={`admin-record-delete-${t.tx_id}`} title="Delete record"
        className="ml-2 inline-flex items-center rounded-lg border border-[#212638] p-1.5 text-[#94A3B8] transition-colors hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-400">
        <Trash2 size={13} />
      </button>
    </td>
  </tr>
);

const STATUS_BADGE = { open: "bg-amber-500/10 text-amber-400", answered: "bg-sky-500/10 text-sky-400", resolved: "bg-emerald-500/10 text-emerald-400" };

const Ticket = ({ m, reload }) => {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const status = m.status || "open";

  const send = async (resolve) => {
    setBusy(true);
    try {
      await api.post(`/admin/contact-messages/${m.ticket_id}/reply`, { message: reply, resolve });
      toast.success(resolve ? "Reply sent and resolved" : "Reply sent");
      setReply("");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const resolve = () => api.post(`/admin/contact-messages/${m.ticket_id}/resolve`).then(() => { toast.success("Marked resolved"); reload(); }).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="rounded-xl border border-[#212638] bg-[#0F111A] p-4" data-testid={`admin-contact-${m.ticket_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold">{m.subject} <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_BADGE[status]}`} data-testid={`admin-contact-status-${m.ticket_id}`}>{status}</span></p>
        <p className="font-mono text-xs text-[#64748B]">{utc(m.created_at)}</p>
      </div>
      <p className="mt-1 text-xs text-[#94A3B8]">{m.user_name} · <a href={`mailto:${m.user_email}`} className="text-[#F7931A] hover:underline">{m.user_email}</a></p>
      <p className="mt-2 whitespace-pre-wrap text-sm text-[#CBD5E1]">{m.message}</p>
      {(m.replies || []).map((r, i) => (
        <div key={i} className="mt-3 border-l-2 border-[#F7931A] pl-3" data-testid={`admin-contact-reply-${m.ticket_id}-${i}`}>
          <p className="text-xs text-[#64748B]">{r.by} · {utc(r.at)}</p>
          <p className="whitespace-pre-wrap text-sm text-[#CBD5E1]">{r.message}</p>
        </div>
      ))}
      {status !== "resolved" && (
        <div className="mt-3 space-y-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply — it's emailed to the user" rows={2}
            data-testid={`admin-contact-reply-input-${m.ticket_id}`}
            className="w-full resize-none rounded-xl border border-[#212638] bg-[#151824] px-3 py-2 text-sm outline-none transition-colors focus:border-[#F7931A]" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => send(false)} disabled={busy || reply.trim().length < 2} data-testid={`admin-contact-send-${m.ticket_id}`}
              className="rounded-lg bg-[#F7931A] px-3 py-1.5 text-xs font-semibold text-[#08090E] transition-colors hover:bg-amber-500 disabled:opacity-50">Send reply</button>
            <button onClick={() => send(true)} disabled={busy || reply.trim().length < 2} data-testid={`admin-contact-send-resolve-${m.ticket_id}`}
              className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">Reply & resolve</button>
            <button onClick={resolve} data-testid={`admin-contact-resolve-${m.ticket_id}`}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">Mark resolved</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ContactInbox = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("open");
  const load = useCallback(() => api.get(`/admin/contact-messages?status=${status}`).then((r) => setItems(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="card p-5 md:p-6" data-testid="admin-contact-inbox">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold tracking-tight">Contact messages</h3>
        <div className="flex gap-1">
          {["open", "resolved", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`admin-contact-filter-${s}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${status === s ? "bg-[#F7931A] text-[#08090E]" : "text-[#94A3B8] hover:bg-[#1C2030] hover:text-white"}`}>{s}</button>
          ))}
        </div>
      </div>
      {items.length === 0 ? <p className="py-6 text-center text-sm text-[#64748B]" data-testid="admin-contact-empty">No messages.</p> : (
        <div className="space-y-3">{items.map((m) => <Ticket key={m.ticket_id} m={m} reload={load} />)}</div>
      )}
    </section>
  );
};

export default function AdminRecords() {
  const { user, refresh } = useAuth();
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeText, setWipeText] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [inboxKey, setInboxKey] = useState(0);

  const resetAll = async () => {
    try {
      const { data } = await api.post("/admin/reset-all", { confirm: resetText });
      setItems([]);
      toast.success(`App reset: ${data.users_reset} users set to $0 / 0 BTC`);
      setInboxKey((k) => k + 1);
      setInboxKey((k) => k + 1);
      loadStats();
      refresh();
      setResetOpen(false);
      setResetText("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const wipeAll = async () => {
    try {
      const { data } = await api.post("/admin/transactions/delete-all", { confirm: wipeText });
      setItems([]);
      toast.success(`Deleted ${data.deleted.transactions} transaction records`);
      loadStats();
      setWipeOpen(false);
      setWipeText("");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/transactions/${toDelete.tx_id}`);
      setItems((xs) => xs.filter((x) => x.tx_id !== toDelete.tx_id));
      toast.success("Record deleted");
      loadStats();
    } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  const load = useCallback(() => {
    const params = new URLSearchParams({ type, ...(q.trim() ? { q: q.trim() } : {}) });
    api.get(`/admin/transactions?${params}`).then((r) => setItems(r.data)).catch(() => {});
  }, [type, q]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load, user]);
  useEffect(() => { if (user?.is_admin) loadStats(); }, [user, loadStats]);

  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="grain min-h-screen bg-[#08090E]">
      <Header />
      <main className="relative z-10 mx-auto max-w-7xl space-y-6 px-4 pb-16 pt-24 md:px-8 md:pt-28">
        <div className="fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F7931A]">Admin</p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Transaction records</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">Everything runs automatically. Every receipt, user and signed timestamp is kept here.</p>
        </div>
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard label="Users" value={stats.users} testid="admin-stat-users" />
            <StatCard label="Transactions" value={stats.transactions} testid="admin-stat-transactions" />
            <StatCard label="Trade volume" value={fmtUsd(stats.volume_usd)} testid="admin-stat-volume" />
            <StatCard label="Withdrawn" value={fmtUsd(stats.withdrawn_usd)} testid="admin-stat-withdrawn" />
            <StatCard label="Fees collected" value={fmtUsd(stats.fees_collected)} testid="admin-stat-fees" />
          </div>
        )}
        <section className="card p-5 md:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setType(f)} data-testid={`admin-filter-${f}`}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${type === f ? "bg-[#F7931A] text-[#08090E]" : "text-[#94A3B8] hover:bg-[#1C2030] hover:text-white"}`}>{f}</button>
              ))}
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <button onClick={() => setResetOpen(true)} data-testid="admin-reset-all-button"
              className="inline-flex items-center gap-1.5 rounded-xl bg-red-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-400">
              <RotateCcw size={13} /> Reset app data
            </button>
            <button onClick={() => setWipeOpen(true)} data-testid="admin-delete-all-button"
              className="inline-flex items-center gap-1.5 rounded-xl border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-400 transition-colors hover:bg-red-500/10">
              <Trash2 size={13} /> Delete all records
            </button>
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username or email" data-testid="admin-search-input"
                className="w-full rounded-xl border border-[#212638] bg-[#0F111A] py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-[#F7931A]" />
            </div>
            </div>
          </div>
          <div className="overflow-x-auto" data-testid="admin-records-table">
            {items.length === 0 ? <p className="py-10 text-center text-sm text-[#64748B]" data-testid="admin-records-empty">No records found.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-[0.15em] text-[#64748B]">
                  <th className="pb-3">User</th><th className="pb-3">Type</th><th className="pb-3">USD</th>
                  <th className="hidden pb-3 lg:table-cell">BTC</th><th className="hidden pb-3 md:table-cell">Method</th>
                  <th className="pb-3">Timestamp</th><th className="hidden pb-3 xl:table-cell">Signature</th><th className="pb-3 text-right">Receipt</th>
                </tr></thead>
                <tbody>{items.map((t) => <RecordRow key={t.tx_id} t={t} onDelete={setToDelete} />)}</tbody>
              </table>
            )}
          </div>
        </section>
        <FeeReport reloadKey={`${inboxKey}-${items.length}`} />
        <ContactInbox key={inboxKey} />
        <AlertDialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetText(""); }}>
          <AlertDialogContent className="border-[#212638] bg-[#151824] text-[#F8FAFC]" data-testid="admin-reset-all-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset all app data?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#94A3B8]">
                Sets every user's BTC and USD balance to 0 and permanently deletes all transactions, withdrawals, payments, price alerts, auto-buy plans (card subscriptions are cancelled), notifications, support chats and contact messages. User accounts and sign-ins are kept. Type <span className="font-mono font-semibold text-red-400">RESET</span> to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" data-testid="admin-reset-all-input"
              className="w-full rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5 font-mono text-sm outline-none transition-colors focus:border-red-500" />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-reset-all-cancel" className="border-[#212638] bg-transparent text-[#F8FAFC] hover:bg-[#1C2030] hover:text-white">Cancel</AlertDialogCancel>
              <button onClick={resetAll} disabled={resetText !== "RESET"} data-testid="admin-reset-all-confirm"
                className="rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-400 disabled:opacity-50">Reset everything</button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={wipeOpen} onOpenChange={(o) => { setWipeOpen(o); if (!o) setWipeText(""); }}>
          <AlertDialogContent className="border-[#212638] bg-[#151824] text-[#F8FAFC]" data-testid="admin-delete-all-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete ALL transaction records?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#94A3B8]">
                This permanently removes every transaction, withdrawal and payment record for all users, including receipts. User accounts and balances are not changed. Type <span className="font-mono font-semibold text-red-400">DELETE ALL</span> to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <input value={wipeText} onChange={(e) => setWipeText(e.target.value)} placeholder="DELETE ALL" data-testid="admin-delete-all-input"
              className="w-full rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5 font-mono text-sm outline-none transition-colors focus:border-red-500" />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-delete-all-cancel" className="border-[#212638] bg-transparent text-[#F8FAFC] hover:bg-[#1C2030] hover:text-white">Cancel</AlertDialogCancel>
              <button onClick={wipeAll} disabled={wipeText !== "DELETE ALL"} data-testid="admin-delete-all-confirm"
                className="rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-400 disabled:opacity-50">Delete everything</button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
          <AlertDialogContent className="border-[#212638] bg-[#151824] text-[#F8FAFC]" data-testid="admin-delete-record-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this record?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#94A3B8]">
                {toDelete && <>{toDelete.type} of {fmtUsd(toDelete.usd_amount)} by {toDelete.user_name} ({toDelete.tx_id}). </>}
                This permanently removes the record and its receipt. User balances are not changed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-delete-record-cancel" className="border-[#212638] bg-transparent text-[#F8FAFC] hover:bg-[#1C2030] hover:text-white">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} data-testid="admin-delete-record-confirm" className="bg-red-500 text-white hover:bg-red-400">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
